import { z } from "zod";

import { MetadataSchema } from "api/schemas/common";
import { UserCourseLessonSchema } from "api/schemas/UserCourse";

import { LocaleSchema, StateSchema, TargetJointsSchema, VisibilitySchema } from "./shared";

const ContentTypeSchema = z.enum(["heading", "image", "paragraph"]);
export type ContentType = z.infer<typeof ContentTypeSchema>;

const ContentSchema = z.object({
  type: ContentTypeSchema,
  content: z.string().min(1),
});
export type Content = z.infer<typeof ContentSchema>;

const AnswerSchema = z.object({
  text: z.string().min(1),
  correct: z.boolean(),
});

const QuestionSchema = z.object({
  title: z.string().min(1),
  answers: z.array(AnswerSchema),
  tip_description: z.string().optional(),
});
export type Question = z.infer<typeof QuestionSchema>;

const TranslationSchema = z.object({
  content: z.array(ContentSchema),
  created_at: z.string(),
  id: z.number(),
  image_url: z.string().nullable(),
  locale: LocaleSchema,
  questions: z.array(QuestionSchema),
  text_lesson_part_id: z.number(),
  title: z.string(),
});
export type Translation = z.infer<typeof TranslationSchema>;

const PartQuestionSchema = z.object({
  order: z.number(),
  correct_alternative: z.number(),
});
export type PartQuestion = z.infer<typeof PartQuestionSchema>;

const PartSchema = z.object({
  created_at: z.string(),
  deprecated_at: z.string().nullable(),
  id: z.number(),
  order: z.number(),
  questions: z.array(PartQuestionSchema),
  text_lesson_id: z.number(),
  translation: TranslationSchema,
  updated_at: z.string(),
});
export type Part = z.infer<typeof PartSchema>;

export const LessonSchema = z.object({
  created_at: z.string(),
  creator_id: z.number().nullable(),
  deleted_at: z.string().nullable(),
  id: z.number(),
  name: z.string(),
  parts: z.array(PartSchema),
  state: StateSchema,
  target_joints: z.array(TargetJointsSchema),
  updated_at: z.string(),
  visibility: VisibilitySchema,
});
export type Lesson = z.infer<typeof LessonSchema>;

const PostLessonPayloadSchema = z.object({
  target_joints: z.array(TargetJointsSchema),
});
export type PostLessonPayload = z.infer<typeof PostLessonPayloadSchema>;
export const PostLessonResponseSchema = LessonSchema.omit({ parts: true });

const PostLessonPartPayloadSchema = z.object({
  order: z.number().min(1),
  questions: z.array(
    z.object({
      order: z.number(),
      correct_alternative: z.number(),
    })
  ),
});
export type PostLessonPartPayload = z.infer<typeof PostLessonPartPayloadSchema>;
export const PostLessonPartResponseSchema = z.object({
  ...PartSchema.omit({
    deprecated_at: true,
    text_lesson_id: true,
    translation: true,
  }).shape,
  lesson_id: z.number(),
});

const PostLessonPartTranslationPayloadSchema = z.object({
  content: z.array(ContentSchema),
  image_url: z.string().nullable().optional(),
  locale: LocaleSchema,
  questions: z.array(QuestionSchema),
  title: z.string().min(1),
});
export type PostLessonPartTranslationPayload = z.infer<typeof PostLessonPartTranslationPayloadSchema>;
export const PostLessonPartTranslationResponseSchema = TranslationSchema;

const PostLessonPartArgsSchema = z.object({
  lessonID: z.number(),
  data: PostLessonPartPayloadSchema,
});
export type PostLessonPartArgs = z.infer<typeof PostLessonPartArgsSchema>;

const PostLessonPartTranslationArgsSchema = z.object({
  lessonID: z.number(),
  lessonPartID: z.number(),
  data: PostLessonPartTranslationPayloadSchema,
});
export type PostLessonPartTranslationArgs = z.infer<typeof PostLessonPartTranslationArgsSchema>;

const PutLessonArgsSchema = z.object({
  lessonID: z.number(),
  data: PostLessonPayloadSchema,
});
export type PutLessonArgs = z.infer<typeof PutLessonArgsSchema>;

const PutLessonPartArgsSchema = z.object({
  lessonID: z.number(),
  lessonPartID: z.number(),
  data: PostLessonPartPayloadSchema,
});
export type PutLessonPartArgs = z.infer<typeof PutLessonPartArgsSchema>;

const PutLessonPartTranslationArgsSchema = z.object({
  lessonID: z.number(),
  lessonPartID: z.number(),
  lessonPartTranslationID: z.number(),
  data: PostLessonPartTranslationPayloadSchema,
});
export type PutLessonPartTranslationArgs = z.infer<typeof PutLessonPartTranslationArgsSchema>;

export const GetLessonsResponseSchema = z.object({
  items: z.array(UserCourseLessonSchema),
  metadata: MetadataSchema,
});

export type GetLessonsResponse = z.infer<typeof GetLessonsResponseSchema>;

// FORM SCHEMAS AND TYPES

export const LessonFormDataSchema = z.object({
  ...PostLessonPayloadSchema.shape,
  id: z.number().optional(),
  parts: z.array(
    z.object({
      ...PostLessonPartPayloadSchema.shape,
      id: z.number().optional(),
      translation: z.object({
        ...PostLessonPartTranslationPayloadSchema.shape,
        id: z.number().optional(),
      }),
    })
  ),
});
export type LessonFormData = z.infer<typeof LessonFormDataSchema>;

// END OF FORM SCHEMAS AND TYPES
